import React, { useCallback, useState } from 'react';
import { Alert, Button, Card, Collapse, Space, Typography } from 'antd';
import { SessionError } from '../components/SessionProvider';
import { CloseOutlined, ToolOutlined, WarningOutlined } from '@ant-design/icons';
import { useSession } from '../contexts/SessionContext';

interface ErrorFixProps {
  onFix: (errors: SessionError[]) => Promise<void>;
  hanging?: boolean;
}

const ErrorFix: React.FC<ErrorFixProps> = ({ onFix, hanging = false }) => {
  const [activeKey, setActiveKey] = useState<string | string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(true);
  const { sessionErrors, clearErrors } = useSession();

  const handleFix = useCallback(async () => {
    setLoading(true);
    await onFix(sessionErrors);
    setLoading(false);
    clearErrors && clearErrors();
  }, [sessionErrors, clearErrors]);

  if (!sessionErrors || sessionErrors.length === 0) {
    return null;
  }

  return (
    <>
      <Button
        type="primary"
        shape="circle"
        size="large"
        icon={<WarningOutlined />}
        onClick={() => setVisible(!visible)}
        style={{
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        }}
      />
      <Card
        title={
          <Space>
            <Typography.Text type="danger">潜在问题已检测到</Typography.Text>
            <Typography.Text type="secondary">{sessionErrors.length} 个错误</Typography.Text>
          </Space>
        }
        extra={<Button type="text" icon={<CloseOutlined />} onClick={() => setVisible(false)} />}
        style={{
          width: 500,
          position: 'fixed',
          right: 20,
          bottom: 20,
          zIndex: 1000,
          boxShadow: '0 3px 6px rgba(0,0,0,0.16)',
          display: visible ? 'block' : 'none',
        }}
      >
        <Collapse
          activeKey={activeKey}
          onChange={setActiveKey}
          items={sessionErrors.map((error, index) => ({
            key: index,
            label: (
              <Space>
                <Typography.Text type="danger">{error.type === 'terminal' ? '终端错误' : '浏览器错误'}</Typography.Text>
                {error.from && (
                  <Typography.Text ellipsis style={{ maxWidth: 300 }}>
                    {error.from}
                  </Typography.Text>
                )}
              </Space>
            ),
            children: (
              <div>
                <Alert type="error" message={error.message} />
              </div>
            ),
          }))}
        />
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Space>
            <Button disabled={hanging} onClick={handleFix} loading={loading} type="primary" icon={<ToolOutlined />}>
              尝试修复
            </Button>
            <Button onClick={() => setVisible(false)}>忽略</Button>
          </Space>
        </div>
      </Card>
    </>
  );
};

export default ErrorFix;
